import type { AiAnnotationDraft, AiProposalEngine } from "@/ai/engines/engine.types";
import { groupId, groupLabel, groupMidpointSec, inferProposalTimeSec, proposalHasKeyword } from "@/ai/engines/engineUtils";

const NPC_KEYWORDS = ["npc", "runtime", "hook", "choice", "reaction", "state"];

export const npcEngine: AiProposalEngine = (context) => {
  const drafts: AiAnnotationDraft[] = [];
  const clipIsNpc = context.selectedClip?.kind === "npc" || context.selectedClipLabel.toLowerCase().includes("npc");

  for (const proposal of context.proposals) {
    if (!clipIsNpc && !proposalHasKeyword(proposal, NPC_KEYWORDS)) continue;

    const runtimeLike = proposalHasKeyword(proposal, ["runtime", "hook", "choice", "reaction"]);
    const timeSec = inferProposalTimeSec(context, proposal);

    drafts.push({
      type: "npc",
      subtype: runtimeLike ? "runtime_hook" : "npc_state_transition",
      severity: runtimeLike ? "warning" : "suggestion",
      summary: proposal.rationale.trim() || "NPC state review suggested.",
      details: runtimeLike
        ? {
            hookType: proposalHasKeyword(proposal, ["choice"]) ? "choice" : proposalHasKeyword(proposal, ["reaction"]) ? "reaction" : "sync",
            exposedState: context.selectedClipLabel,
            nodeId: proposal.id,
          }
        : {
            npcId: context.selectedClipId,
            stateIn: null,
            stateOut: proposal.rationale.trim() || null,
            triggerFrame: Math.floor(timeSec * context.fps),
          },
      proposedAction: runtimeLike ? "expose_runtime_hook" : "adjust",
      nodeId: proposal.id,
      timeSec,
      frame: Math.floor(timeSec * context.fps),
      action: runtimeLike ? "Expose runtime hook for this NPC beat" : "Review NPC transition",
    });
  }

  for (const [index, group] of context.groups.entries()) {
    const label = groupLabel(group);
    if (!label || !NPC_KEYWORDS.some((keyword) => label.toLowerCase().includes(keyword))) continue;

    const timeSec = groupMidpointSec(group, context.anchorTimeSec);
    drafts.push({
      type: "npc",
      subtype: label.toLowerCase().includes("runtime") ? "runtime_hook" : "npc_continuity_warning",
      severity: "suggestion",
      summary: `${label} may need NPC runtime wiring.`,
      details: label.toLowerCase().includes("runtime")
        ? {
            hookType: "sync",
            exposedState: label,
            nodeId: groupId(group, `npc-${index}`),
          }
        : {
            expectedState: label,
            observedBehavior: null,
            arcReference: context.narrativeSummary || null,
          },
      proposedAction: label.toLowerCase().includes("runtime") ? "expose_runtime_hook" : "review",
      nodeId: `group:${groupId(group, `npc-${index}`)}`,
      timeSec,
      frame: Math.floor(timeSec * context.fps),
      action: label.toLowerCase().includes("runtime") ? "Expose NPC runtime hook" : "Review NPC continuity",
    });
  }

  return drafts;
};